<template>
	<div class="ks-date-picker" :class="{ 'ks-date-picker-no-title': noTitle }">
		<!-- Title -->
		<div v-if="!noTitle" class="ks-date-picker-title">
			<div class="ks-date-picker-title-year" @click="activePicker = 'YEAR'">{{ displayYear }}</div>
			<div class="ks-date-picker-title-date" @click="activePicker = 'DATE'">{{ displayDate }}</div>
		</div>

		<!-- Header -->
		<div class="ks-date-picker-header">
			<button class="ks-date-picker-nav-btn" @click="prev">
				<span class="material-icons">chevron_left</span>
			</button>
			<button class="ks-date-picker-header-text" @click="togglePicker">
				{{ headerText }}
			</button>
			<button class="ks-date-picker-nav-btn" @click="next">
				<span class="material-icons">chevron_right</span>
			</button>
		</div>

		<!-- Year picker -->
		<div v-if="activePicker === 'YEAR'" class="ks-date-picker-years" ref="yearList">
			<button
				v-for="year in yearRange"
				:key="year"
				class="ks-date-picker-year-btn"
				:class="{ 'ks-date-picker-year-active': year === tableYear }"
				@click="selectYear(year)"
			>{{ year }}</button>
		</div>

		<!-- Month picker -->
		<div v-else-if="activePicker === 'MONTH'" class="ks-date-picker-months">
			<button
				v-for="(month, i) in monthNames"
				:key="i"
				class="ks-date-picker-month-btn"
				:class="{ 'ks-date-picker-month-active': isMonthSelected(i), 'ks-date-picker-month-disabled': !isMonthAllowed(i) }"
				:disabled="!isMonthAllowed(i)"
				@click="selectMonth(i)"
			>{{ month }}</button>
		</div>

		<!-- Date picker -->
		<div v-else class="ks-date-picker-dates">
			<div class="ks-date-picker-weekdays">
				<span v-for="(day, i) in weekdayLabels" :key="i" class="ks-date-picker-weekday">{{ day }}</span>
			</div>
			<div class="ks-date-picker-days">
				<span v-for="(blank, i) in blankDays" :key="'b' + i" class="ks-date-picker-day-blank"></span>
				<button
					v-for="day in daysInMonth"
					:key="day"
					class="ks-date-picker-day-btn"
					:class="{ 'ks-date-picker-day-selected': isDaySelected(day), 'ks-date-picker-day-today': isDayToday(day), 'ks-date-picker-day-disabled': !isDayAllowed(day) }"
					:disabled="!isDayAllowed(day)"
					@click="selectDay(day)"
				>{{ day }}</button>
			</div>
		</div>

		<!-- Actions slot -->
		<div v-if="$slots.default" class="ks-date-picker-actions">
			<slot></slot>
		</div>
	</div>
</template>

<script>
var MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
var MONTH_NAMES_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
var DAY_NAMES = ["S", "M", "T", "W", "T", "F", "S"]

export default {
	name: "KDatePicker",
	props: {
		value: {
			type: String,
			default: ""
		},
		type: {
			type: String,
			default: "date" // "date" or "month"
		},
		noTitle: {
			type: Boolean,
			default: false
		},
		scrollable: {
			type: Boolean,
			default: false
		},
		allowedDates: {
			type: Function,
			default: null
		},
		min: {
			type: String,
			default: ""
		},
		max: {
			type: String,
			default: ""
		},
		firstDayOfWeek: {
			type: [String, Number],
			default: 1
		},
		locale: {
			type: String,
			default: "en"
		},
		readonly: {
			type: Boolean,
			default: false
		},
		color: {
			type: String,
			default: ""
		}
	},
	data () {
		var now = new Date()
		var parsed = this.parseValue(this.value)
		return {
			tableYear: parsed ? parsed.year : now.getFullYear(),
			tableMonth: parsed ? parsed.month : now.getMonth(),
			activePicker: this.type === "month" ? "MONTH" : "DATE"
		}
	},
	computed: {
		firstDay () {
			return Number(this.firstDayOfWeek) || 0
		},
		weekdayLabels () {
			var days = DAY_NAMES.slice()
			var offset = this.firstDay
			return days.slice(offset).concat(days.slice(0, offset))
		},
		headerText () {
			if (this.activePicker === "YEAR") {
				return this.yearRange[0] + " - " + this.yearRange[this.yearRange.length - 1]
			}
			if (this.activePicker === "MONTH" || this.type === "month") {
				return String(this.tableYear)
			}
			return MONTH_NAMES_FULL[this.tableMonth] + " " + this.tableYear
		},
		displayYear () {
			var parsed = this.parseValue(this.value)
			return parsed ? parsed.year : new Date().getFullYear()
		},
		displayDate () {
			var parsed = this.parseValue(this.value)
			if (!parsed) return "--"
			if (this.type === "month") {
				return MONTH_NAMES_FULL[parsed.month]
			}
			return DAY_NAMES[new Date(parsed.year, parsed.month, parsed.day).getDay()] + ", " + MONTH_NAMES[parsed.month] + " " + parsed.day
		},
		blankDays () {
			var firstOfMonth = new Date(this.tableYear, this.tableMonth, 1).getDay()
			var blanks = firstOfMonth - this.firstDay
			if (blanks < 0) blanks += 7
			return blanks
		},
		daysInMonth () {
			return new Date(this.tableYear, this.tableMonth + 1, 0).getDate()
		},
		yearRange () {
			var years = []
			var start = this.tableYear - 50
			var end = this.tableYear + 50
			for (var y = start; y <= end; y++) {
				years.push(y)
			}
			return years
		},
		monthNames () {
			return MONTH_NAMES
		}
	},
	watch: {
		value (val) {
			var parsed = this.parseValue(val)
			if (parsed) {
				this.tableYear = parsed.year
				this.tableMonth = parsed.month
			}
		},
		activePicker (val) {
			if (val === "YEAR") {
				this.$nextTick(() => {
					this.scrollToActiveYear()
				})
			}
		}
	},
	methods: {
		parseValue (val) {
			if (!val) return null
			var parts = val.split("-")
			if (parts.length >= 2) {
				return {
					year: parseInt(parts[0], 10),
					month: parseInt(parts[1], 10) - 1,
					day: parts[2] ? parseInt(parts[2], 10) : 1
				}
			}
			return null
		},
		formatDate (year, month, day) {
			var m = String(month + 1).padStart(2, "0")
			if (this.type === "month") {
				return year + "-" + m
			}
			var d = String(day).padStart(2, "0")
			return year + "-" + m + "-" + d
		},
		togglePicker () {
			if (this.activePicker === "DATE") {
				this.activePicker = "MONTH"
			} else if (this.activePicker === "MONTH") {
				this.activePicker = "YEAR"
			} else {
				this.activePicker = this.type === "month" ? "MONTH" : "DATE"
			}
		},
		prev () {
			if (this.activePicker === "YEAR") {
				this.tableYear -= 100
			} else if (this.activePicker === "MONTH" || this.type === "month") {
				this.tableYear--
			} else {
				if (this.tableMonth === 0) {
					this.tableMonth = 11
					this.tableYear--
				} else {
					this.tableMonth--
				}
			}
		},
		next () {
			if (this.activePicker === "YEAR") {
				this.tableYear += 100
			} else if (this.activePicker === "MONTH" || this.type === "month") {
				this.tableYear++
			} else {
				if (this.tableMonth === 11) {
					this.tableMonth = 0
					this.tableYear++
				} else {
					this.tableMonth++
				}
			}
		},
		selectYear (year) {
			this.tableYear = year
			this.activePicker = this.type === "month" ? "MONTH" : "MONTH"
		},
		selectMonth (month) {
			this.tableMonth = month
			if (this.type === "month") {
				this.$emit("input", this.formatDate(this.tableYear, month, 1))
				return
			}
			this.activePicker = "DATE"
		},
		selectDay (day) {
			if (this.readonly) return
			var dateStr = this.formatDate(this.tableYear, this.tableMonth, day)
			this.$emit("input", dateStr)
		},
		isDaySelected (day) {
			var parsed = this.parseValue(this.value)
			if (!parsed) return false
			return parsed.year === this.tableYear && parsed.month === this.tableMonth && parsed.day === day
		},
		isDayToday (day) {
			var now = new Date()
			return now.getFullYear() === this.tableYear && now.getMonth() === this.tableMonth && now.getDate() === day
		},
		isDayAllowed (day) {
			var dateStr = this.formatDate(this.tableYear, this.tableMonth, day)
			if (this.min && dateStr < this.min) return false
			if (this.max && dateStr > this.max) return false
			if (this.allowedDates) return this.allowedDates(dateStr)
			return true
		},
		isMonthSelected (month) {
			var parsed = this.parseValue(this.value)
			if (!parsed) return false
			if (this.type === "month") {
				return parsed.year === this.tableYear && parsed.month === month
			}
			return this.tableYear === parsed.year && month === parsed.month
		},
		isMonthAllowed (month) {
			if (this.min) {
				var minDate = this.min.substring(0, 7)
				var thisMonth = this.tableYear + "-" + String(month + 1).padStart(2, "0")
				if (thisMonth < minDate) return false
			}
			if (this.max) {
				var maxDate = this.max.substring(0, 7)
				var thisMonth2 = this.tableYear + "-" + String(month + 1).padStart(2, "0")
				if (thisMonth2 > maxDate) return false
			}
			return true
		},
		scrollToActiveYear () {
			var el = this.$refs.yearList
			if (!el) return
			var active = el.querySelector(".ks-date-picker-year-active")
			if (active) {
				active.scrollIntoView({ block: "center" })
			}
		}
	}
}
</script>

<style>
.ks-date-picker {
	width: 290px;
	background: transparent;
	border-radius: 4px;
	overflow: hidden;
	user-select: none;
}

.ks-date-picker-title {
	padding: 16px;
	background: var(--ks-col-accent, #FFD317);
	color: var(--ks-background-color, #111820);
}

.ks-date-picker-title-year {
	font-size: 14px;
	opacity: 0.7;
	cursor: pointer;
}

.ks-date-picker-title-date {
	font-size: 24px;
	font-weight: 500;
	cursor: pointer;
}

.ks-date-picker-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
}

.ks-date-picker-header-text {
	font-size: 14px;
	font-weight: 500;
	color: var(--ks-font-color-primary, #e3eaf1);
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px 8px;
	border-radius: 4px;
}

.ks-date-picker-header-text:hover {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-date-picker-nav-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: var(--ks-font-color-primary, #e3eaf1);
	border-radius: 50%;
	cursor: pointer;
}

.ks-date-picker-nav-btn:hover {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

/* Weekdays */
.ks-date-picker-weekdays {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	text-align: center;
	padding: 0 12px;
}

.ks-date-picker-weekday {
	font-size: 11px;
	color: var(--ks-font-color-secondary, rgba(255, 255, 255, 0.5));
	padding: 4px 0;
}

/* Days */
.ks-date-picker-days {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	padding: 4px 12px 12px;
	gap: 2px;
}

.ks-date-picker-day-blank {
	width: 36px;
	height: 36px;
}

.ks-date-picker-day-btn {
	width: 36px;
	height: 36px;
	border: none;
	background: transparent;
	color: var(--ks-font-color-primary, #e3eaf1);
	border-radius: 50%;
	font-size: 12px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin: auto;
}

.ks-date-picker-day-btn:hover:not(:disabled) {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-date-picker-day-selected {
	background: var(--ks-col-accent, #FFD317) !important;
	color: var(--ks-background-color, #111820) !important;
	font-weight: 600;
}

.ks-date-picker-day-today:not(.ks-date-picker-day-selected) {
	border: 1px solid var(--ks-col-accent, #FFD317);
	color: var(--ks-col-accent, #FFD317);
}

.ks-date-picker-day-disabled {
	opacity: 0.3;
	cursor: default;
}

/* Months */
.ks-date-picker-months {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	padding: 12px;
	gap: 8px;
}

.ks-date-picker-month-btn {
	padding: 10px 4px;
	border: none;
	background: transparent;
	color: var(--ks-font-color-primary, #e3eaf1);
	border-radius: 16px;
	font-size: 13px;
	cursor: pointer;
}

.ks-date-picker-month-btn:hover:not(:disabled) {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-date-picker-month-active {
	background: var(--ks-col-accent, #FFD317) !important;
	color: var(--ks-background-color, #111820) !important;
	font-weight: 600;
}

.ks-date-picker-month-disabled {
	opacity: 0.3;
	cursor: default;
}

/* Years */
.ks-date-picker-years {
	height: 250px;
	overflow-y: auto;
	padding: 8px 12px;
	text-align: center;
}

.ks-date-picker-year-btn {
	display: block;
	width: 100%;
	padding: 8px;
	border: none;
	background: transparent;
	color: var(--ks-font-color-primary, #e3eaf1);
	font-size: 16px;
	cursor: pointer;
	border-radius: 4px;
}

.ks-date-picker-year-btn:hover {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-date-picker-year-active {
	font-size: 22px;
	font-weight: 600;
	color: var(--ks-col-accent, #FFD317);
}

/* Actions */
.ks-date-picker-actions {
	display: flex;
	justify-content: flex-end;
	padding: 8px 12px;
	border-top: 1px solid var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}
</style>
